import { OnboardingOptionPill } from "@/features/onboarding/components/onboarding-option-pill";
import { OnboardingPillButton } from "@/features/onboarding/components/onboarding-pill-button";
import { OnboardingStepShellV5 } from "@/features/onboarding/components/onboarding-step-shell-v5";
import { useGetFitnessProfile } from "@/hooks/requests/fitness-profile/use-get-fitness-profile";
import { useUpdateFitnessProfile } from "@/hooks/requests/fitness-profile/use-update-fitness-profile";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { triggerHaptic } from "@/utils/haptics";
import { useEffect, useRef, useState } from "react";

const ICONS_DIR = "/assets/images/onboarding-v5/icons";

const OPTIONS = [
  { id: "strength-training", label: "Strength training", icon: `${ICONS_DIR}/move-strength.svg` },
  { id: "running", label: "Running", icon: `${ICONS_DIR}/move-running.svg` },
  { id: "walking-steps", label: "Walking / Steps", icon: `${ICONS_DIR}/move-walking.svg` },
  { id: "cycling", label: "Cycling", icon: `${ICONS_DIR}/move-cycling.svg` },
  { id: "hiit-conditioning", label: "HIIT / Conditioning", icon: `${ICONS_DIR}/move-hiit.svg` },
  { id: "pilates-yoga", label: "Pilates / Yoga", icon: `${ICONS_DIR}/move-yoga.svg` },
  { id: "classes-sport", label: "Classes / Sport", icon: `${ICONS_DIR}/move-sport.svg` },
  { id: "something-else", label: "Something else", icon: `${ICONS_DIR}/move-something-else.svg` },
];

const MovePage = () => {
  const { navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const { fitnessProfile } = useGetFitnessProfile();
  const { updateFitnessProfileAsync, isUpdating } = useUpdateFitnessProfile();
  const { showError } = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (hydratedRef.current) return;
    if (fitnessProfile?.moveTypes?.length) {
      setSelected(new Set(fitnessProfile.moveTypes));
      hydratedRef.current = true;
    }
  }, [fitnessProfile?.moveTypes]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleContinue = async () => {
    if (selected.size === 0 || isUpdating) return;
    triggerHaptic();
    try {
      // Only this screen's field — PUT merges, so the other answers are preserved.
      await Promise.all([
        preloadNextRoute(),
        updateFitnessProfileAsync({ moveTypes: Array.from(selected) }),
      ]);
      navigateToNextStep();
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  return (
    <OnboardingStepShellV5
      title="How do you like to move?"
      subtitle="Choose everything that applies"
      footer={
        <OnboardingPillButton
          text="Continue"
          onClick={handleContinue}
          disabled={selected.size === 0 || isUpdating}
        />
      }
    >
      <div className="flex flex-col gap-4">
        {OPTIONS.map((option) => (
          <OnboardingOptionPill
            key={option.id}
            label={option.label}
            icon={option.icon}
            selected={selected.has(option.id)}
            onClick={() => toggle(option.id)}
          />
        ))}
      </div>
    </OnboardingStepShellV5>
  );
};

export default MovePage;
